import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Sparkles, Calendar, User, GraduationCap, RefreshCw } from "lucide-react";

interface EnrollmentResult {
  activityName: string;
  billingType: string;
  scheduledDays?: number[] | null;
}

interface SuccessStepProps {
  studentName: string;
  studentClass: string;
  enrollmentResults: EnrollmentResult[];
  hasErrors?: boolean;
  onReset: () => void;
}

const dayNames = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

export function SuccessStep({ 
  studentName, 
  studentClass, 
  enrollmentResults, 
  hasErrors = false, 
  onReset 
}: SuccessStepProps) {
  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Success Animation Header */}
      <div className="text-center space-y-6">
        <div className="relative inline-flex items-center justify-center">
          {/* Main success icon with pulse animation */}
          <div className="w-24 h-24 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center shadow-2xl animate-pulse">
            <CheckCircle className="w-12 h-12 text-white animate-bounce" />
          </div>
          
          {/* Sparkle animations */}
          <div className="absolute -top-2 -right-2 w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center animate-ping">
            <Sparkles className="w-3 h-3 text-white" />
          </div>
          <div className="absolute -bottom-2 -left-2 w-4 h-4 bg-blue-400 rounded-full flex items-center justify-center animate-ping animation-delay-200">
            <Sparkles className="w-2 h-2 text-white" />
          </div>
          <div className="absolute top-0 right-6 w-3 h-3 bg-purple-400 rounded-full animate-ping animation-delay-500"></div>
        </div>
        
        <div className="space-y-3">
          <h1 className="text-4xl font-bold text-gray-900 animate-fade-in-up">
            ¡Inscripción Completada!
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto animate-fade-in-up animation-delay-300">
            {studentName} ha sido inscrito/a exitosamente en {enrollmentResults.length} actividad{enrollmentResults.length > 1 ? 'es' : ''}
            {hasErrors && (
              <span className="block text-orange-600 font-medium mt-2">
                ⚠️ Algunas inscripciones requieren verificación adicional
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Student Information Card */}
      <Card className="shadow-2xl border-0 bg-gradient-to-br from-white to-green-50/30 animate-slide-up animation-delay-500">
        <CardHeader className="pb-6">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <User className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <CardTitle className="text-2xl text-gray-900">Datos del Estudiante</CardTitle>
              <CardDescription className="text-gray-600">
                Información confirmada en el sistema
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center space-x-3 p-4 bg-white/70 rounded-xl">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <User className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Estudiante</span>
                <p className="font-semibold text-gray-900">{studentName}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3 p-4 bg-white/70 rounded-xl">
              <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                <GraduationCap className="w-4 h-4 text-purple-600" />
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Clase</span>
                <p className="font-semibold text-gray-900">{studentClass}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Activities Success Cards */}
      <Card className="shadow-2xl border-0 bg-gradient-to-br from-white to-emerald-50/30 animate-slide-up animation-delay-700">
        <CardHeader className="pb-6">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <CardTitle className="text-2xl text-gray-900">
                Actividades Confirmadas
                <span className="ml-2 text-lg text-green-600">({enrollmentResults.length})</span>
              </CardTitle>
              <CardDescription className="text-gray-600">
                Inscripciones procesadas exitosamente
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {enrollmentResults.map((result, index) => (
            <div key={index} className="bg-white/70 border border-green-100 rounded-xl p-5 space-y-4 animate-fade-in-up" style={{animationDelay: `${800 + index * 100}ms`}}>
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center text-white font-bold">
                    {index + 1}
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">{result.activityName}</h4>
                    <Badge 
                      variant="secondary" 
                      className="mt-1 bg-blue-100 text-blue-700 border-blue-200"
                    >
                      {result.billingType === 'per_session' ? 'Uso días sueltos' : 'Uso mensual'}
                    </Badge>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Badge className="bg-green-100 text-green-700 border-green-200 hover:bg-green-200">
                    ✓ Inscrito/a
                  </Badge>
                </div>
              </div>
              
              {result.scheduledDays && result.scheduledDays.length > 0 && (
                <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-100">
                  <div className="w-6 h-6 bg-blue-500 rounded-md flex items-center justify-center">
                    <Calendar className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-sm font-medium text-blue-900">Días programados:</span>
                  <div className="flex gap-1 flex-wrap">
                    {result.scheduledDays.map((day: number) => (
                      <Badge key={day} variant="outline" className="text-xs bg-white border-blue-200 text-blue-700">
                        {dayNames[day]}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Next Steps Information */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-6 shadow-lg animate-fade-in-up animation-delay-1000">
        <div className="text-center space-y-4">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-500 rounded-xl">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div className="space-y-2">
            <h3 className="text-xl font-semibold text-blue-900">¡Todo listo!</h3>
            <p className="text-blue-800 leading-relaxed max-w-2xl mx-auto">
              Tu hijo/a ha sido inscrito en las actividades correctamente.
            </p>
          </div>
          
          <div className="pt-4">
            <Button 
              onClick={onReset} 
              variant="outline" 
              className="h-12 px-8 rounded-xl border-blue-200 text-blue-600 hover:bg-blue-50 hover:border-blue-300 transition-all duration-300 transform hover:scale-105"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Inscribir otro estudiante
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}